import React from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { type PositionProps } from 'styled-system'
import { Flex } from 'components/Layout'
import { Loader } from 'components/Loader'
import { Text } from 'components/Typography'
import {
  BORDER_WIDTH,
  BOX_SHADOW,
  COLOR_INTENT,
  FONT_SIZE,
  FONT_WEIGHT,
  GRADIENT,
  LINE_HEIGHT,
  RADIUS,
  SPACE,
  Z_INDEX,
} from 'Theme'

type SuggestionValue = string | { [key: string]: any }

export type Suggestion<T = string> = {
  value: T
  label: string
}

interface SuggestionsDropdownProps<T> extends Pick<PositionProps, 'top'> {
  data: Suggestion<T>[]
  isLoading?: boolean
  onSelect: (option: Suggestion<T>) => void
}

const OptionsMenu = styled(Flex)`
  position: absolute;
  width: 100%;
  z-index: ${Z_INDEX.SELECT};
  flex-direction: column;
  border: ${BORDER_WIDTH.PX_1} solid ${COLOR_INTENT.GRAY_10};
  border-radius: ${RADIUS.PX_6};
  color: ${COLOR_INTENT.GRAY_90};
  box-shadow: ${BOX_SHADOW.ELEVATION_6};
  background: linear-gradient(${GRADIENT.GRADIENT_1}),
    ${COLOR_INTENT.SELECT.MENU_BACKGROUND};
  padding: ${SPACE.PX_5};
  gap: ${SPACE.PX_10};
  max-height: ${SPACE.PX_160};
  overflow: auto;

  transition: max-height 0.2s ease-in-out;
`

const Option = styled(Flex)`
  cursor: pointer;
  border-radius: ${RADIUS.PX_3};
  font-size: ${FONT_SIZE.PX_14};
  font-weight: ${FONT_WEIGHT.NORMAL};
  padding: ${SPACE.PX_5} ${SPACE.PX_15};
  overflow: 'hidden';
  text-overflow: 'ellipsis';
  white-space: 'nowrap';
  line-height: ${LINE_HEIGHT.L};

  &:hover {
    background-color: ${COLOR_INTENT.SELECT.OPTION_BACKGROUND_HOVER};
    ${Text} {
      color: ${COLOR_INTENT.SELECT.OPTION_LABEL_HOVER};
    }
    svg {
      path {
        fill: ${COLOR_INTENT.TEXT_ON_DARK};
      }
    }
  }
`

export const SuggestionsDropdown = <T extends SuggestionValue>({
  data,
  isLoading,
  onSelect,
  top,
}: SuggestionsDropdownProps<T>) => {
  if (!data.length && !isLoading) {
    return null
  }

  return (
    <OptionsMenu
      data-test-id={DATA_TEST_ID.INPUT_WITH_SUGGESTIONS_MENU}
      top={top}
    >
      {isLoading ? (
        <Flex
          width="100%"
          p={`${SPACE.PX_10} ${SPACE.PX_12}`}
          justifyContent="center"
          data-test-id={DATA_TEST_ID.INPUT_WITH_SUGGESTIONS_MENU_LOADER}
        >
          <Loader />
        </Flex>
      ) : (
        data.map(({ label, value }, index) => (
          <Option
            key={`${label}-${index}`}
            onClick={() => {
              onSelect({ label, value })
            }}
            data-test-id={DATA_TEST_ID.INPUT_WITH_SUGGESTIONS_MENU_OPTION}
          >
            <Text>{label}</Text>
          </Option>
        ))
      )}
    </OptionsMenu>
  )
}
